/**
 * File system module
 * Handles file drops, service worker registration, and file watching
 */

import {
  addToCache,
  analyzeProject,
  clearCache,
  clearFs,
  extractEntries,
  fileDropped,
  getFile,
  getFileContent,
  registerServiceWorker,
} from '@jscadui/fs-provider'
import { pickEntry } from '@jscadui/agent-loop'
import { inlineIncludes } from '@jscadui/worker/src/inlineIncludes.js'
import { collectProjectFiles } from './projectFiles.js'
import { shouldAllowReload } from './reloadDetection.js'

/**
 * @typedef {import('@jscadui/fs-provider').SwHandler} SwHandler
 */

/**
 * @typedef {object} FileSystemDeps
 * @property {() => void} onFilesChange - Called when files change (for script re-run)
 * @property {(files: string[]) => void} setEditorFiles - Set files in editor
 * @property {(files: string[]) => void} onFilesChanged - Notify editor of changed files
 * @property {(error: unknown) => void} setError - Set error display
 * @property {(alias: Array<{name: string, path: string}>) => void} onAliasFound - Called when package aliases found
 * @property {(script: string, url: string) => void} onScriptReady - Called when script is ready to run
 * @property {(projectName: string) => void} setProjectName - Set export project name
 * @property {(script: string) => string} addV1Shim - Add v1 compatibility shim
 * @property {(files: string[]) => Promise<void>} clearFileCache - Clear worker file cache
 */

/** @type {SwHandler | undefined} */
let sw

/** @type {Object.<string, FileSystemFileHandle>} */
let saveMap = {}

// The project's files when no service worker registered, so models still run.
/** @type {Map<string, unknown>} */
const memoryFiles = new Map()

/**
 * Reset file references
 * @param {(files: string[]) => void} setEditorFiles
 */
export async function resetFileRefs(setEditorFiles) {
  setEditorFiles([])
  saveMap = {}
  if (sw) {
    delete sw.fileToRun
    await clearFs(sw)
  }
}

/**
 * Initialize the file system service worker
 * @param {FileSystemDeps} deps
 */
export async function initFs(deps) {
  const { setEditorFiles, onFilesChanged, clearFileCache, onFilesChange } = deps

  /**
   * @param {string} path
   * @param {SwHandler} swHandler
   */
  const getFileWrapper = (path, swHandler) => {
    const file = getFileContent(path, swHandler)
    // Notify editor of active files
    file.then(() => setEditorFiles(swHandler.filesToCheck)).catch(err => {
      console.error('Failed to get file content:', path, err)
    })
    return file
  }

  const scope = document.location.pathname
  try {
    sw = await registerServiceWorker(`bundle.fs-serviceworker.js?prefix=${scope}swfs/`, getFileWrapper, {
      scope,
      prefix: scope + 'swfs/',
    })
  } catch (err) {
    // C7 fix: Service worker registration failed - track reload attempt and propagate error
    // shouldAllowReload() tracks that we tried, then re-throw for caller to handle
    shouldAllowReload()
    console.error('Service worker registration failed:', err)
    throw err
  }

  if (sw) {
    sw.defProjectName = 'jscad'
    sw.onfileschange = files => {
      if (files.includes('/package.json')) {
        onFilesChange()
      } else {
        clearFileCache(files)
        onFilesChanged(files)
        if (sw.fileToRun) onFilesChange()
      }
    }
    sw.getFile = path => getFile(path, sw)
  }
}

/**
 * Inline V1 include()s for a project file from the project's file map
 * (keys with or without a leading slash, as memoryFiles and the service
 * worker cache each store them). Unresolvable includes throw; callers fall
 * back to the raw script so the run fails at include(), as before.
 */
export async function inlineProjectIncludes(script, fileToRun, files) {
  const readFile = (p) => files[p] ?? files[p.replace(/^\//, '')]
  const fromFile = '/' + fileToRun.replace(/^\//, '')
  return inlineIncludes(script, fromFile, { readFile })
}

/**
 * Reload the current project
 * @param {FileSystemDeps} deps
 */
export async function reloadProject(deps) {
  if (!sw) return

  const { setEditorFiles, setProjectName, onAliasFound, onScriptReady, addV1Shim } = deps

  clearCache(sw.cache)
  saveMap = {}
  sw.filesToCheck = []

  const result = await analyzeProject(sw)
  const { alias } = result
  let { script } = result

  setProjectName(sw.projectName)

  if (alias.length) {
    onAliasFound(alias)
  }

  const url = sw.fileToRun

  // Inject jscad v1 shim, and also inject changed script to cache
  // so worker and editor have the same code
  if (sw.fileToRun?.endsWith('.jscad')) {
    // V1 include() merges project files into global scope; inline first so
    // the shim below sees one translation unit. Unresolvable includes fall
    // back to the raw script (the run then fails at include(), as before).
    try {
      script = await inlineProjectIncludes(script, sw.fileToRun, await projectFiles())
    } catch (e) {
      console.warn('v1 include inlining skipped:', e.message)
    }
    script = addV1Shim(script)
    // onScriptReady sends the cache to the frame, so the shimmed source has to
    // be in it by then or the frame compiles the source without the shim.
    await addToCache(sw.cache, sw.fileToRun, script)
  }

  onScriptReady(script, url)
  setEditorFiles(sw.filesToCheck)
}

/** @type {import('@jscadui/fs-provider').PickEntry} */
const pickDroppedEntry = (names, context) => pickEntry(names, { ...context, anyJs: true })

/**
 * Handle file drop
 * @param {DataTransfer} dataTransfer
 * @param {FileSystemDeps} deps
 * @param {Array<import('@jscadui/fs-provider').FSEntry>} [entries] entries already
 *   read from `dataTransfer`. A real DataTransfer is cleared once the drop
 *   event unwinds, so callers must extract once, up front, and pass them.
 */
export async function handleFileDrop(dataTransfer, deps, entries) {
  const { setEditorFiles, setError, onFilesChange } = deps

  try {
    const files = entries ?? await extractEntries(dataTransfer)
    if (!files.length) return

    await resetFileRefs(setEditorFiles)
    if (!sw) await initFs(deps)
    await fileDropped(sw, files, pickDroppedEntry)
    onFilesChange()
  } catch (error) {
    setError(error)
    console.error(error)
  }
}

/**
 * Get the current service worker handler
 * @returns {SwHandler | undefined}
 */
export function getSwHandler() {
  return sw
}

/**
 * Add content to cache
 * @param {string} path
 * @param {string} content
 */
export async function addToCacheWrapper(path, content) {
  if (sw) await addToCache(sw.cache, path, content)
  else memoryFiles.set(path.replace(/^\//, ''), content)
}

/**
 * Drop one file from the cache.
 * @param {string} path
 */
export async function removeFromCache(path) {
  memoryFiles.delete(path.replace(/^\//, ''))
  if (sw) await sw.cache.delete(new Request(path))
}

/**
 * Empty the file cache. The frame is sent whatever the cache holds, so a
 * project's files have to go before the next project's arrive.
 */
export async function clearProjectCache() {
  memoryFiles.clear()
  if (sw) await clearCache(sw.cache)
}

/**
 * The open project's files, keyed by bare path: what every run sends the frame.
 * @returns {Promise<Record<string, unknown>>}
 */
export async function projectFiles() {
  return sw ? collectProjectFiles(sw) : Object.fromEntries(memoryFiles)
}

/**
 * Get the save map (file handle references)
 * @returns {Object.<string, FileSystemFileHandle>}
 */
export function getSaveMap() {
  return saveMap
}

/**
 * Set a file handle in the save map
 * @param {string} path
 * @param {FileSystemFileHandle} handle
 */
export function setSaveMapEntry(path, handle) {
  saveMap[path] = handle
}

/**
 * Create file watcher interval
 * H2 fix: Returns cleanup function instead of relying solely on beforeunload
 * @param {(files: string[]) => void} onFilesChanged
 * @param {() => void} runScript
 * @returns {{ interval: NodeJS.Timeout, cleanup: () => void }}
 */
export function createFileWatcher(onFilesChanged, runScript) {
  const interval = setInterval(async () => {
    for (const p in saveMap) {
      const handle = saveMap[p]
      try {
        const file = await handle.getFile()
        if (file.lastModified > handle.lastMod) {
          handle.lastMod = file.lastModified
          await onFilesChanged([file])
          runScript()
        }
      } catch (err) {
        // H3 fix: Handle errors in file watching (file may have been deleted)
        console.warn('Error checking file:', p, err)
      }
    }
  }, 500)

  // Cleanup function that can be called explicitly
  const cleanup = () => {
    clearInterval(interval)
  }

  // Also clean up on page unload as fallback
  window.addEventListener('beforeunload', cleanup)

  return { interval, cleanup }
}

/**
 * Setup drag and drop handlers
 * @param {HTMLElement} dropModal
 * @param {(dataTransfer: DataTransfer, target: EventTarget | null) => Promise<void>} onDrop
 * @returns {() => void} removes the body listeners again
 */
export function setupDragDrop(dropModal, onDrop) {
  /** @type {number | NodeJS.Timeout | undefined} */
  let showDropTimer

  const showDrop = (show) => {
    clearTimeout(showDropTimer)
    dropModal.style.display = show ? 'initial' : 'none'
  }

  const onBodyDrop = async ev => {
    ev.preventDefault()
    if (ev.dataTransfer === null) return
    showDrop(false)
    await onDrop(ev.dataTransfer, ev.target)
  }

  const onBodyDragOver = ev => {
    ev.preventDefault()
    // A project row has its own drop handlers; the full-screen hint would
    // cover it (and, without pointer-events: none, capture the drop).
    if (ev.target?.closest?.('[data-project-id]')) { showDrop(false); return }
    showDrop(true)
  }

  const dragEndOrLeave = () => {
    clearTimeout(showDropTimer)
    showDropTimer = setTimeout(() => {
      showDrop(false)
    }, 300)
  }

  document.body.addEventListener('drop', onBodyDrop)
  document.body.addEventListener('dragover', onBodyDragOver)
  document.body.addEventListener('dragend', dragEndOrLeave)
  document.body.addEventListener('dragleave', dragEndOrLeave)
  return () => {
    document.body.removeEventListener('drop', onBodyDrop)
    document.body.removeEventListener('dragover', onBodyDragOver)
    document.body.removeEventListener('dragend', dragEndOrLeave)
    document.body.removeEventListener('dragleave', dragEndOrLeave)
  }
}
